// useRegionMap.js — Leaflet 지도 초기화·마커·오버레이 로직 훅
// RegionCoverage.jsx에서 분리 (300줄 초과 해소)
//
// 반환:
//   mapContainerRef   — Leaflet 마운트 대상 div ref
//   overlayDivRef     — 전국지도 오버레이 div ref (터치 핸들러 등록용)
//   imgTransform      — 이미지 현재 transform { x, y, scale }
//   highlightedRegion — 강조된 지역명 (null = 없음)
//   mapOverlayActive  — 전국지도 오버레이 표시 여부 (토글)
//   toggleOverlay     — 오버레이 열기/닫기 (초기 transform 설정 포함)
//   handleListClick   — 목록 항목 클릭 핸들러
//   handleReset       — 전체 보기 리셋

import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import {
  KOREA_CENTER, DEFAULT_ZOOM, getMarkerSize,
  createRegionIcon, buildTimelineChars, getOpportunityColor, findMinZoomToSeparate,
} from './helpers';

export function useRegionMap({ regions, colorRules, mobileTab, setMobileTab, isMobile }) {
  // 강조된 지역명 (null = 없음)
  const [highlightedRegion, setHighlightedRegion] = useState(null);
  // 전국지도 오버레이 활성 여부
  const [mapOverlayActive, setMapOverlayActive]   = useState(false);
  // 이미지 transform 상태 — 터치로 조작, 오버레이 열릴 때 Leaflet 뷰로 초기화
  const [imgTransform, setImgTransform] = useState({ x: 0, y: 0, scale: 1 });

  // Leaflet 참조
  const mapContainerRef = useRef(null); // Leaflet 마운트 대상 DOM
  const mapRef          = useRef(null); // L.map 인스턴스
  const layerRef        = useRef(null); // L.layerGroup (마커 모음)
  const markerMapRef    = useRef({});   // { [region]: L.marker } — 목록 클릭 flyTo용

  // 이미지 오버레이 터치 핸들링 참조
  const overlayDivRef   = useRef(null); // 오버레이 div — 터치 이벤트 등록 대상
  const imgTransformRef = useRef({ x: 0, y: 0, scale: 1 }); // 핸들러 내 stale closure 방지
  const touchRef        = useRef(null); // 현재 터치 제스처 상태

  // ── 현재 Leaflet 뷰 → 이미지 초기 transform 계산 ────────────────────────────
  // 이미지는 DEFAULT_ZOOM + KOREA_CENTER 기준으로 제작 → project()로 픽셀 오프셋 계산
  // transformOrigin: 0 0 기준 — imgX, imgY가 화면 중앙에 오도록 translate
  function calcInitialTransform() {
    const map = mapRef.current;
    if (!map) return { x: 0, y: 0, scale: 1 };
    const zoom   = map.getZoom();
    const center = map.getCenter();
    const size   = map.getSize();
    const S      = Math.pow(2, zoom - DEFAULT_ZOOM);

    const pCenter  = map.project([center.lat, center.lng], DEFAULT_ZOOM);
    const pDefault = map.project(KOREA_CENTER,             DEFAULT_ZOOM);

    // 이미지 내 현재 center의 픽셀 좌표 (이미지 좌상단 = 0,0)
    const imgX = (pCenter.x - pDefault.x) + size.x / 2;
    const imgY = (pCenter.y - pDefault.y) + size.y / 2;

    return { x: size.x / 2 - imgX * S, y: size.y / 2 - imgY * S, scale: S };
  }

  // ── imgTransform → Leaflet 역산: 이미지 현재 뷰를 Leaflet 지도에 반영 ─────────
  function syncLeafletFromImage() {
    const map = mapRef.current;
    if (!map) return;
    const { x, y, scale } = imgTransformRef.current;
    const size = map.getSize();

    // 이미지 공간에서 화면 중앙에 해당하는 픽셀 좌표
    const imgCenterX = (size.x / 2 - x) / scale;
    const imgCenterY = (size.y / 2 - y) / scale;

    // 이미지 픽셀 → 위도경도 역산 (calcInitialTransform의 역연산)
    const pDefault = map.project(KOREA_CENTER, DEFAULT_ZOOM);
    const pNewCenter = L.point(
      pDefault.x + imgCenterX - size.x / 2,
      pDefault.y + imgCenterY - size.y / 2,
    );
    const newCenter = map.unproject(pNewCenter, DEFAULT_ZOOM);
    const newZoom   = DEFAULT_ZOOM + Math.log2(scale);

    map.setView(newCenter, newZoom, { animate: false });
  }

  // ── 오버레이 토글 ──────────────────────────────────────────────────────────────
  // 열릴 때: Leaflet 뷰 → 이미지 transform 초기화
  // 닫힐 때: 이미지 transform → Leaflet 지도 역산 적용
  function toggleOverlay() {
    setMapOverlayActive((prev) => {
      if (!prev) {
        // 열릴 때: 현재 지도 뷰를 이미지 초기 transform으로 설정
        const t = calcInitialTransform();
        imgTransformRef.current = t;
        setImgTransform(t);
      } else {
        // 닫힐 때: 이미지 현재 뷰를 Leaflet에 반영
        syncLeafletFromImage();
      }
      return !prev;
    });
  }

  // ── 이미지 오버레이 터치 핸들러 등록 (passive:false — preventDefault 필수) ──
  // 브라우저 뷰포트 핀치줌 완전 차단 → Leaflet처럼 이미지에만 transform 적용
  useEffect(() => {
    const el = overlayDivRef.current;
    if (!el) return;

    function getTouchDist(t) {
      const dx = t[0].clientX - t[1].clientX;
      const dy = t[0].clientY - t[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    }

    function onStart(e) {
      e.preventDefault();
      const { x, y, scale } = imgTransformRef.current;
      if (e.touches.length === 2) {
        // 핀치줌: 두 손가락 중점 기준 확대/축소
        touchRef.current = {
          type: 'pinch', dist: getTouchDist(e.touches), scale, tx: x, ty: y,
          mid: {
            x: (e.touches[0].clientX + e.touches[1].clientX) / 2,
            y: (e.touches[0].clientY + e.touches[1].clientY) / 2,
          },
        };
      } else {
        // 패닝: 한 손가락 이동
        touchRef.current = { type: 'pan', cx: e.touches[0].clientX, cy: e.touches[0].clientY, tx: x, ty: y, scale };
      }
    }

    function onMove(e) {
      e.preventDefault();
      const st = touchRef.current;
      if (!st) return;
      let next;
      if (st.type === 'pinch' && e.touches.length >= 2) {
        const newDist  = getTouchDist(e.touches);
        const newScale = Math.max(0.5, Math.min(10, st.scale * newDist / st.dist));
        const newMid   = {
          x: (e.touches[0].clientX + e.touches[1].clientX) / 2,
          y: (e.touches[0].clientY + e.touches[1].clientY) / 2,
        };
        // 초기 중점이 이미지 공간에서 같은 위치에 유지되도록 translate 계산
        const imgMx = (st.mid.x - st.tx) / st.scale;
        const imgMy = (st.mid.y - st.ty) / st.scale;
        next = { x: newMid.x - imgMx * newScale, y: newMid.y - imgMy * newScale, scale: newScale };
      } else if (st.type === 'pan' && e.touches.length === 1) {
        next = { x: st.tx + e.touches[0].clientX - st.cx, y: st.ty + e.touches[0].clientY - st.cy, scale: st.scale };
      }
      if (next) { imgTransformRef.current = next; setImgTransform(next); }
    }

    function onEnd(e) { e.preventDefault(); touchRef.current = null; }

    el.addEventListener('touchstart',  onStart, { passive: false });
    el.addEventListener('touchmove',   onMove,  { passive: false });
    el.addEventListener('touchend',    onEnd,   { passive: false });
    el.addEventListener('touchcancel', onEnd,   { passive: false });
    return () => {
      el.removeEventListener('touchstart',  onStart);
      el.removeEventListener('touchmove',   onMove);
      el.removeEventListener('touchend',    onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, []); // empty deps — 모든 상태는 ref로 접근해 stale closure 없음

  // ── Leaflet 지도 초기화 ────────────────────────────────────────────────────────

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = L.map(mapContainerRef.current, {
      center: KOREA_CENTER,
      zoom:   DEFAULT_ZOOM,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      maxZoom: 18,
    }).addTo(map);

    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current   = map;

    return () => {
      map.remove();
      mapRef.current   = null;
      layerRef.current = null;
    };
  }, []);

  // ── 마커 렌더링 (regions / highlightedRegion / colorRules 변경 시 갱신) ──────────

  useEffect(() => {
    if (!mapRef.current || !layerRef.current) return;

    // 기존 마커 전체 제거 후 재생성
    layerRef.current.clearLayers();
    markerMapRef.current = {};

    regions.forEach((r) => {
      const lat = parseFloat(r.avg_lat);
      const lng = parseFloat(r.avg_lng);
      if (!lat || !lng) return; // 좌표 없는 지역은 생략

      const isHighlighted = r.region === highlightedRegion;
      const icon          = createRegionIcon(
        r.days_since_last !== null ? Number(r.days_since_last) : null,
        Number(r.visit_days_in_period),
        isHighlighted,
        colorRules,
      );

      const marker = L.marker([lat, lng], { icon });

      // 툴팁: 지역명 + 타임라인 + 요약 정보
      const dsl    = r.days_since_last !== null ? Number(r.days_since_last) : null;
      const vdays  = Number(r.visit_days_in_period);
      const color  = getOpportunityColor(dsl, vdays, colorRules);
      const dslStr = dsl === 0 ? '오늘' : dsl === null ? '-' : `${dsl}일 전`;

      const tlChars = buildTimelineChars(r.visit_dates_in_period);
      const tlHtml  = tlChars.join('')
        .replace(/:/g, `<span style="color:${color};font-weight:900;">:</span>`);

      const tooltipContent = `
        <div style="font-size:13px;line-height:1.8;white-space:nowrap;">
          <strong>${r.region}</strong><br>
          <span style="font-size:11px;color:var(--text-muted);">${dslStr} · ${vdays}일 방문</span><br>
          <span style="font-family:Arial,sans-serif;font-size:11px;letter-spacing:0;color:var(--text-muted);">30 ${tlHtml} 0</span>
        </div>
      `;
      marker.bindTooltip(tooltipContent, {
        direction: 'top',
        offset:    [0, -Math.round(getMarkerSize(Number(r.visit_days_in_period)) / 2) - 4],
      });

      // 마커 클릭 → 강조 토글 + 겹침 해소 스마트 줌 이동
      marker.on('click', () => {
        setHighlightedRegion((prev) => (prev === r.region ? null : r.region));
        const targetZoom = findMinZoomToSeparate(mapRef.current, r, regions);
        if (targetZoom === null) {
          mapRef.current.panTo([lat, lng], { animate: true, duration: 0.5 });
        } else {
          mapRef.current.flyTo([lat, lng], targetZoom, { animate: true, duration: 0.6 });
        }
        const regionKey = r.region;
        mapRef.current.once('moveend', () => markerMapRef.current[regionKey]?.openTooltip());
      });

      marker.addTo(layerRef.current);
      markerMapRef.current[r.region] = marker;
    });
  }, [regions, highlightedRegion, colorRules]);

  // ── 모바일: 지도 탭 전환 시 Leaflet 크기 재계산 ──────────────────────────────────

  useEffect(() => {
    if (mobileTab === 'map' && mapRef.current) {
      setTimeout(() => mapRef.current?.invalidateSize(), 50);
    }
  }, [mobileTab]);

  // ── 핸들러 ──────────────────────────────────────────────────────────────────────

  /** 목록 항목 클릭 → 강조 토글 + 지도 이동 (모바일: 지도 탭 전환) */
  function handleListClick(r) {
    setHighlightedRegion((prev) => (prev === r.region ? null : r.region));
    const lat = parseFloat(r.avg_lat);
    const lng = parseFloat(r.avg_lng);
    if (!mapRef.current || !lat || !lng) return;

    const targetZoom = findMinZoomToSeparate(mapRef.current, r, regions);
    if (targetZoom === null) {
      mapRef.current.panTo([lat, lng], { animate: true, duration: 0.5 });
    } else {
      mapRef.current.flyTo([lat, lng], targetZoom, { animate: true, duration: 0.6 });
    }
    if (isMobile) setMobileTab('map');
  }

  /** 전체 보기 리셋 */
  function handleReset() {
    setHighlightedRegion(null);
    if (mapRef.current) {
      mapRef.current.flyTo(KOREA_CENTER, DEFAULT_ZOOM, { animate: true, duration: 0.7 });
    }
  }

  return {
    mapContainerRef,
    overlayDivRef,
    imgTransform,
    highlightedRegion,
    mapOverlayActive,
    setMapOverlayActive,
    toggleOverlay,
    handleListClick,
    handleReset,
  };
}
